// src/oura/components/newscan/MoreOptions.tsx
//
// The Business step's "More options" fold: Malay and Chinese questions
// (Auto / On / Off), rivals staff already know, and other names customers use
// for the business. Folded, its head says what is set ("Languages: Malay
// Auto, Chinese Auto · 1 rival · other names"), so nothing hides in it.
//
// Rivals and other names change the counting, not the questions. The
// business's `notUs` names are not edited here and are never touched: the
// caller patches only the keys this fold changes.

import { useId } from "react";
import type { LanguageMode, ScanDraftBusiness } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { DrawerFold, TYPE } from "@oura/ui";
import { Field } from "@oura/components/onboarding/fields";
import { LANGUAGE_MODE_LABEL, plural } from "@oura/newscan/copy";
import { Muted } from "@oura/components/newscan/Muted";
import { RadioSeg } from "@oura/components/newscan/RadioSeg";
import { DEF_LABEL_COLUMN } from "@oura/components/newscan/DefRow";
import { TagInput } from "@oura/components/newscan/TagInput";

type MorePatch = Partial<Pick<ScanDraftBusiness, "languages" | "rivals" | "otherNames">>;

export interface MoreOptionsProps {
    languages: ScanDraftBusiness["languages"];
    rivals: string[];
    otherNames: string[];
    onChange(patch: MorePatch): void;
    disabled?: boolean;
    /** Found by what it sells (Online): the language rows replace buyer-problem rows. */
    brand?: boolean;
}

const LANGS = [
    { key: "ms", label: "Malay" },
    { key: "zh", label: "Chinese" },
] as const;

const MODES: LanguageMode[] = ["auto", "on", "off"];

const COPY = {
    title: "More options",
    languages: "Languages",
    hint: (mode: LanguageMode, language: string) =>
        mode === "auto"
            ? "Decided from the reviews read in the draft."
            : mode === "on"
              ? `Up to six questions in ${language} when this recipe asks them.`
              : "English only.",
    // Brand mode: one hint for both languages, under the Languages label.
    brandHint: (anyOn: boolean) =>
        anyOn ? "Malay and Chinese questions replace buyer-problem questions, by the share of buyers who search that way." : "English only.",
    rivals: "Rivals you know",
    rivalsPlaceholder: "Optional, a rival you know",
    otherNames: "Other names customers use",
    otherNamesPlaceholder: "Optional, e.g. a short name customers use",
    note: "Rivals and other names change the counting, not the questions.",
    fact: (languages: ScanDraftBusiness["languages"], rivals: number, otherNames: number) =>
        [
            `Languages: Malay ${LANGUAGE_MODE_LABEL[languages.ms]}, Chinese ${LANGUAGE_MODE_LABEL[languages.zh]}`,
            rivals ? plural(rivals, "rival") : "rivals",
            otherNames ? plural(otherNames, "other name") : "other names",
        ].join(" · "),
} as const;

export function MoreOptions({ languages, rivals, otherNames, onChange, disabled, brand }: MoreOptionsProps) {
    const base = useId();
    const brandHintId = `${base}-languages-hint`;
    return (
        <DrawerFold id="newscan.business.more" label={COPY.title} note={COPY.fact(languages, rivals.length, otherNames.length)} remember>
            <div className="space-y-5">
                <fieldset className="m-0 min-w-0 border-0 p-0">
                    <legend className={cn(TYPE.lab, "mb-1.5 p-0")}>{COPY.languages}</legend>
                    {brand && (
                        <Muted as="p" id={brandHintId} className="m-0 mb-1.5">
                            {COPY.brandHint(languages.ms !== "off" || languages.zh !== "off")}
                        </Muted>
                    )}
                    <div className="divide-y divide-gray-100">
                        {LANGS.map((l) => {
                            const labelId = `${base}-${l.key}`;
                            const hintId = `${base}-${l.key}-hint`;
                            return (
                                <div key={l.key} className={cn("grid grid-cols-1 gap-x-6 gap-y-2 py-2.5 first:pt-1", DEF_LABEL_COLUMN)}>
                                    <span id={labelId} className={cn(TYPE.lab, "sm:pt-1.5")}>
                                        {l.label}
                                    </span>
                                    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
                                        <RadioSeg<LanguageMode>
                                            options={MODES.map((m) => ({ key: m, label: LANGUAGE_MODE_LABEL[m] }))}
                                            value={languages[l.key]}
                                            onChange={(m) => onChange({ languages: { ...languages, [l.key]: m } })}
                                            labelledBy={labelId}
                                            describedBy={brand ? brandHintId : hintId}
                                            disabled={disabled}
                                        />
                                        {!brand && (
                                            <Muted id={hintId} className="min-w-0 flex-1 basis-56">
                                                {COPY.hint(languages[l.key], l.label)}
                                            </Muted>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </fieldset>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label={COPY.rivals}>
                        {({ id, describedBy }) => (
                            <TagInput
                                id={id}
                                describedBy={describedBy}
                                values={rivals}
                                onChange={(next) => onChange({ rivals: next })}
                                placeholder={COPY.rivalsPlaceholder}
                                disabled={disabled}
                            />
                        )}
                    </Field>
                    <Field label={COPY.otherNames}>
                        {({ id, describedBy }) => (
                            <TagInput
                                id={id}
                                describedBy={describedBy}
                                values={otherNames}
                                onChange={(next) => onChange({ otherNames: next })}
                                placeholder={COPY.otherNamesPlaceholder}
                                disabled={disabled}
                            />
                        )}
                    </Field>
                </div>
                <Muted as="p" size="tm" tone="ink4">
                    {COPY.note}
                </Muted>
            </div>
        </DrawerFold>
    );
}
